import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { ApplicationAssignment, CreateAssignmentRequest, UpdateAssignmentRequest, PEORepresentativeWithWorkload } from '@/types/assignment.types';
import { toast } from 'sonner';

export const useAssignmentManagement = () => {
  const queryClient = useQueryClient();
  
  // Fetch all assignments (admin view)
  const {
    data: assignments = [],
    isLoading: isLoadingAssignments,
    error: assignmentsError,
  } = useQuery({
    queryKey: ['admin-assignments'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('application_assignments')
        .select(`
          *,
          application:applications!inner (
            id,
            company_legal_name,
            first_name,
            last_name,
            email,
            w2_employee_count,
            primary_industry,
            naics_code,
            status
          ),
          assigned_to_provider:peo_providers!inner (
            id,
            name
          ),
          assigned_to_representative:peo_representatives (
            id,
            user_id,
            first_name,
            last_name,
            email,
            title
          ),
          assigned_by_user:profiles!inner (
            id,
            first_name,
            last_name,
            email
          )
        `)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return (data || []) as unknown as ApplicationAssignment[];
    },
    staleTime: 30000,
  });

  // Fetch available applications for assignment
  const {
    data: availableApplications = [],
    isLoading: isLoadingApplications,
  } = useQuery({
    queryKey: ['available-applications'],
    queryFn: async () => {
      // Get applications that don't have assignments yet and are submitted
      const { data, error } = await supabase
        .from('applications')
        .select('id, company_legal_name, first_name, last_name, email, w2_employee_count, primary_industry, status')
        .eq('status', 'submitted')
        .not('id', 'in', `(${assignments.map(a => a.application_id).join(',') || 'null'})`);
      
      if (error) throw error;
      return data;
    },
    enabled: assignments.length >= 0,
    staleTime: 30000,
  });

  // Fetch PEO representatives with workload
  const {
    data: peoRepresentatives = [],
    isLoading: isLoadingReps,
  } = useQuery({
    queryKey: ['peo-representatives-workload'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('peo_representatives')
        .select(`
          *,
          profiles!inner (
            first_name,
            last_name,
            email
          )
        `)
        .eq('verification_status', 'verified');
      
      if (error) throw error;
      
      // Add workload calculations
      const repsWithWorkload = await Promise.all(
        data.map(async (rep: any) => {
          const { data: workload } = await supabase
            .from('application_assignments')
            .select('assignment_status')
            .eq('assigned_to_representative_id', rep.id);
          
          const active = workload?.filter(w => w.assignment_status === 'in_progress').length || 0;
          const pending = workload?.filter(w => w.assignment_status === 'pending').length || 0;
          
          return {
            ...rep,
            first_name: rep.profiles?.first_name,
            last_name: rep.profiles?.last_name,
            email: rep.profiles?.email,
            active_assignments: active,
            pending_assignments: pending,
            overdue_assignments: 0, // TODO: Calculate based on due_date
          };
        })
      );
      
      return repsWithWorkload as PEORepresentativeWithWorkload[];
    },
    staleTime: 60000,
  });

  // Create assignment mutation
  const createAssignment = useMutation({
    mutationFn: async (assignmentData: CreateAssignmentRequest) => {
      // Add the assigned_by field using auth.uid()
      const { data: session } = await supabase.auth.getSession();
      if (!session.session?.user?.id) {
        throw new Error('User not authenticated');
      }
      
      const insertData = {
        ...assignmentData,
        assigned_by: session.session.user.id,
      };
      
      const { data, error } = await supabase
        .from('application_assignments')
        .insert(insertData)
        .select()
        .single();
      
      if (error) throw error;
      
      // Log the creation activity
      await supabase.rpc('log_assignment_activity', {
        p_assignment_id: data.id,
        p_action_type: 'created',
        p_action_details: assignmentData as any
      });
      
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-assignments'] });
      queryClient.invalidateQueries({ queryKey: ['available-applications'] });
      queryClient.invalidateQueries({ queryKey: ['peo-representatives-workload'] });
      toast.success('Assignment created successfully');
    },
    onError: (error) => {
      console.error('Error creating assignment:', error);
      toast.error('Failed to create assignment');
    },
  });

  // Update assignment mutation
  const updateAssignment = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: UpdateAssignmentRequest }) => {
      const { data, error } = await supabase
        .from('application_assignments')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      
      // Log the update activity
      await supabase.rpc('log_assignment_activity', {
        p_assignment_id: id,
        p_action_type: 'status_changed',
        p_action_details: updates as any
      });
      
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-assignments'] });
      queryClient.invalidateQueries({ queryKey: ['peo-assignments'] });
      toast.success('Assignment updated successfully');
    },
    onError: (error) => {
      console.error('Error updating assignment:', error);
      toast.error('Failed to update assignment');
    },
  });

  return {
    assignments,
    isLoadingAssignments,
    assignmentsError,
    availableApplications,
    isLoadingApplications,
    peoRepresentatives,
    isLoadingReps,
    createAssignment,
    updateAssignment,
  };
};

// Hook for PEO representatives to view their assignments
export const usePEOAssignments = (providerId?: string) => {
  const queryClient = useQueryClient();
  
  const {
    data: myAssignments = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ['peo-assignments', providerId],
    queryFn: async () => {
      console.log('usePEOAssignments: Fetching assignments for providerId:', providerId);
      
      if (!providerId) {
        console.log('usePEOAssignments: No providerId provided, returning empty array');
        return [];
      }
      
      const { data, error } = await supabase
        .from('application_assignments')
        .select(`
          *,
          application:applications (
            id,
            company_legal_name,
            first_name,
            last_name,
            email,
            w2_employee_count,
            primary_industry,
            naics_code,
            status,
            city,
            state,
            phone,
            estimated_annual_payroll,
            current_provider,
            additional_notes
          )
        `)
        .eq('assigned_to_provider_id', providerId)
        .order('created_at', { ascending: false });
      
      if (error) {
        console.error('usePEOAssignments: Error fetching assignments:', error);
        throw error;
      }
      
      console.log('usePEOAssignments: Fetched assignments:', data?.length || 0, 'assignments');
      return (data || []) as unknown as ApplicationAssignment[];
    },
    enabled: !!providerId,
    staleTime: 30000,
  });

  const updateMyAssignment = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: UpdateAssignmentRequest }) => {
      const { data, error } = await supabase
        .from('application_assignments')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      
      // Log the update activity
      await supabase.rpc('log_assignment_activity', {
        p_assignment_id: id,
        p_action_type: 'status_changed',
        p_action_details: updates as any
      });
      
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['peo-assignments', providerId] });
      toast.success('Assignment updated successfully');
    },
    onError: (error) => {
      console.error('Error updating assignment:', error);
      toast.error('Failed to update assignment');
    },
  });

  return {
    assignments: myAssignments,
    isLoading,
    error,
    updateAssignment: updateMyAssignment,
  };
};